import { FC } from 'react';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';

import { ReservationMode, ScheduleWorkingHours, TreezStore } from '@/types';
import { useGapCommerceStore, useStoreHours } from '@/hooks';
import { filterResModAndProviderStoreId, getStoreScheduleInfo } from '@/utils';
import { Badge, Icon, IconSvg } from '@/components';

import classNames from 'classnames';

import styles from './treezstore.module.scss';

interface IStoreRadioItemProps {
  store: TreezStore;
  isSelected?: boolean;
  isOpen?: boolean;
  onSelected: () => {};
}

const StoreRadioItem: FC<IStoreRadioItemProps> = ({
  store,
  isSelected,
  onSelected,
}) => {
  const { store: gcStore } = useGapCommerceStore();

  const pickup_schedules = gcStore?.pickup_schedules?.filter(
    s => s?.stores_provider_id === store?.shortName,
  );

  const closures = gcStore?.closures?.filter(s =>
    filterResModAndProviderStoreId(s, store?.shortName, ReservationMode.Pickup),
  );

  const acceptingOrdersLabel = 'ACCEPTING ORDERS';

  const { isStoreOpen, storeStatusLabel, closureNote, isTemporarilyClosed } =
    useStoreHours({
      workingHours: store?.working_hours,
      gcWorkingHours: pickup_schedules?.[0]
        ?.working_hours as ScheduleWorkingHours[],
      closures: closures as any,
      timezone: gcStore?.timezone ?? store?.timezone,
    });

  const storeSchedule = getStoreScheduleInfo(
    pickup_schedules?.[0]?.working_hours as ScheduleWorkingHours[],
  );

  const hasNoSchedule = storeStatusLabel === 'Hours Not Available';

  return (
    <div className={styles.store_radio_item} onClick={onSelected} role="button">
      <div className={styles.store_radio_header}>
        <input
          checked={isSelected}
          className={styles.store_radio}
          onChange={onSelected}
          type="radio"
        />
        <p>{`${store.name} - ${store.city}, ${store.state}`}</p>
      </div>
      <div className={styles.store_content}>
        <span className={styles.store_content_address}>
          {store.full_address.split(',')[0]}
        </span>
        <div className={styles.store__status}>
          <div className={styles.store__status_content}>
            <Badge
              aria-live="polite"
              className={classNames(styles.store__status_content__badge, {
                [styles.store__status_content__badge_closed]: !isStoreOpen,
              })}
              role="status"
            >
              {isStoreOpen || hasNoSchedule ? 'OPEN' : 'CLOSED'}
            </Badge>
            <span className={styles.store__status_content__message}>
              {storeStatusLabel}
            </span>
          </div>
          <div className={styles.store__status_info}>
            {((!isStoreOpen && !isTemporarilyClosed) || hasNoSchedule) && (
              <Badge
                aria-live="polite"
                className={styles.store__status_info_badge}
                role="status"
              >
                {acceptingOrdersLabel}
              </Badge>
            )}
            <Popover className={styles.store__status_info__help}>
              <PopoverButton aria-label="Show info">
                <IconSvg name="alert-info" />
              </PopoverButton>
              <PopoverPanel
                anchor={{ to: 'top end', gap: 10 }}
                className={styles.store__status_info__help_body}
              >
                {({ close }) => (
                  <>
                    <div className={styles.popover_header}>
                      <h6 className={styles.popover_header__title}>
                        {isTemporarilyClosed
                          ? closureNote?.title ?? 'Temporarily Closed'
                          : 'Store Hours'}
                      </h6>
                      <button
                        aria-label="Close"
                        className={styles.popover_header__close_btn}
                        onClick={e => {
                          e.stopPropagation();
                          close();
                        }}
                      >
                        <Icon name="close" />{' '}
                      </button>
                    </div>
                    <div className={styles.popover_body}>
                      {isTemporarilyClosed ? (
                        <p>{closureNote?.message}</p>
                      ) : (
                        <>
                          {storeSchedule?.length > 0
                            ? storeSchedule?.map((el, index) => {
                                return (
                                  <div
                                    className={
                                      styles.popover_body_store_schedule_container
                                    }
                                    key={`${el?.day}_${index}`}
                                  >
                                    <span>{el?.day}</span>
                                    <span>{el?.period}</span>
                                  </div>
                                );
                              })
                            : 'This store hasn’t provided its operating hours yet.'}
                        </>
                      )}
                    </div>
                  </>
                )}
              </PopoverPanel>
            </Popover>
          </div>
        </div>
        {/*  <span
          className={classNames(styles.status, {
            [styles.closed]: !isOpen,
          })}
        >
          {isOpen ? 'Open now' : 'Closed'}
        </span> */}
      </div>
    </div>
  );
};

export { StoreRadioItem };
